import { Col, Row } from 'antd';
import { useFormikContext } from 'formik';
import { isEmpty, isNil } from 'lodash/fp';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type InsuranceProductDetailsWithPermissionsDataFragment } from '../../../../api/fragments/InsuranceProductDetailsWithPermissionsData';
import TranslatedTextAreaField from '../../../../components/fields/TranslatedTextAreaField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import useTranslatedString from '../../../../utilities/useTranslatedString';
import useInsurerOption from '../../FinanceProductDetailsPage/shared/useInsurerOptions';
import { type InsuranceProductFormValues, colSpan } from './shared';

type MainDetailsProps = {
    updated?: string;
    offset?: string;
    disabled?: boolean;
    insuranceProduct?: InsuranceProductDetailsWithPermissionsDataFragment;
};

const MainDetails = ({ updated, offset, insuranceProduct, disabled = false }: MainDetailsProps) => {
    const { t } = useTranslation('insuranceProductDetails');
    const translated = useTranslatedString();
    const { insuranceProductTypeOptions } = useSystemOptions();
    const { yesNoSwitch } = useSystemSwitchData();
    const { setFieldValue, values } = useFormikContext<InsuranceProductFormValues>();
    const { moduleIdByInsurerId, options, availableInsuranceProductTypeByInsurerId } = useInsurerOption();

    const filteredInsuranceProductTypeOptions = useMemo(() => {
        if (values.insurerId && !isEmpty(availableInsuranceProductTypeByInsurerId)) {
            return insuranceProductTypeOptions.filter(
                option =>
                    !isNil(availableInsuranceProductTypeByInsurerId[values.insurerId]) &&
                    Array.isArray(availableInsuranceProductTypeByInsurerId[values.insurerId]) &&
                    availableInsuranceProductTypeByInsurerId[values.insurerId].includes(option.value)
            );
        }

        return [];
    }, [insuranceProductTypeOptions, availableInsuranceProductTypeByInsurerId, values.insurerId]);

    useEffect(() => {
        if (filteredInsuranceProductTypeOptions.length === 0) {
            setFieldValue('type', null);
        }
    }, [filteredInsuranceProductTypeOptions]);

    const onChange = useCallback(
        value => {
            const moduleId = moduleIdByInsurerId[value];
            setFieldValue('moduleId', moduleId);
        },
        [moduleIdByInsurerId, setFieldValue]
    );

    return (
        <CollapsibleWrapper defaultActiveKey={['mainDetails']}>
            <Panel key="mainDetails" header={t('insuranceProductDetails:subTitles.mainDetails')}>
                <Row gutter={[16, 16]}>
                    <Col {...colSpan}>
                        <FormFields.InputField
                            {...t('insuranceProductDetails:fields.displayName', { returnObjects: true })}
                            disabled={disabled}
                            name="displayName"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.TranslatedInputField
                            {...t('insuranceProductDetails:fields.legalName', { returnObjects: true })}
                            disabled={disabled}
                            name="legalName"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.InputField
                            {...t('insuranceProductDetails:fields.identifier', { returnObjects: true })}
                            disabled={disabled}
                            name="identifier"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.RangePickerField
                            {...t('insuranceProductDetails:fields.period', { returnObjects: true })}
                            disabled={disabled}
                            name="period"
                            required
                        />
                    </Col>
                    {isNil(updated) ? (
                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('insuranceProductDetails:fields.insurer', { returnObjects: true })}
                                disabled={disabled}
                                name="insurerId"
                                onChange={onChange}
                                options={options}
                                required
                                showSearch
                            />
                        </Col>
                    ) : (
                        <Col {...colSpan}>
                            <FormFields.DisplayField
                                {...t('insuranceProductDetails:fields.insurer', { returnObjects: true })}
                                value={translated(insuranceProduct.insurer.legalName)}
                            />
                        </Col>
                    )}
                    <Col {...colSpan}>
                        <TranslatedTextAreaField
                            {...t('insuranceProductDetails:fields.description', { returnObjects: true })}
                            disabled={disabled}
                            name="description"
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.SwitchField
                            {...t('insuranceProductDetails:fields.active', { returnObjects: true })}
                            disabled={disabled}
                            name="isActive"
                            {...yesNoSwitch}
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.SelectField
                            {...t('insuranceProductDetails:fields.kind', { returnObjects: true })}
                            disabled={disabled}
                            name="type"
                            options={filteredInsuranceProductTypeOptions}
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.InputNumberField
                            {...t('insuranceProductDetails:fields.orderNo', { returnObjects: true })}
                            disabled={disabled}
                            max={100}
                            min={0}
                            name="order"
                            required
                        />
                    </Col>
                    {updated && (
                        <Col {...colSpan}>
                            <FormFields.DisplayFieldWithUTCOffset
                                {...t('common:fields.updated', { returnObjects: true, offset })}
                                value={updated}
                            />
                        </Col>
                    )}
                </Row>
            </Panel>
        </CollapsibleWrapper>
    );
};

export default MainDetails;
